import React, { useEffect, useState, useCallback } from "react";
import { Box, Flex } from "@acko-tech/building-blocks.ui.common";
import { connect } from "react-redux";
import {getWidgetSpecificProps, getWidgetSpecificIsVisible, getRequestpayload, executeActions} from "@utils/index";
import { useOnload } from "@utils/useOnLoad";
import { analyticEvents as triggerAnalyticEvent } from "@actions/analyticEvents";
import {
  wrapperBaseStyles,
  buttonBaseStyles,
  collapsedButtonBaseStyles,
  iconOnlyButtonBaseStyles,
  menuWrapperBaseStyles,
  menuCardBaseStyles,
  menuItemBaseStyles,
  closeButtonBaseStyles,
  Label,
  IconWrapper,
  IconImage,
} from "./styles";

const DEFAULT_ICON_STYLES = "width: 16px; height: 16px;";
const DEFAULT_CLOSE_ICON_STYLES = "width: 12px; height: 12px;";

const mapStateToProps = (state: any, ownProps: any) => {
  const normalisedConfig = state.configuration?.normalisedConfig;
  const id = ownProps.id;
  return {
    ...getWidgetSpecificProps(normalisedConfig, id),
    is_visible: getWidgetSpecificIsVisible(normalisedConfig, id),
  };
};

export const openChatbot = (args?: any) => {
  if (typeof window === "undefined") {
    console.warn("openChatbot: This action can only be executed in the browser");
    return;
  }
  const copilot = (window as any).copilot;
  if (copilot && typeof copilot.open === "function") {
    copilot.open();
  } else {
    console.warn(
      "openChatbot: Copilot is not available."
    );
  }
};

const FloatingIcon = (props: any) => {
  const {
    text,
    icon,
    iconAlt = "icon",
    actions = {},
    is_visible,
    styles = {},
    buttonStyles = {},
    iconWrapperStyles,
    textStyles,
    iconStyles,
    collapseAfterMs = 2000,
    collapsedButtonStyles = {},
    promptButtonStyles = {},
    iconButtonStyles = {},
    promptText = "Talk to us",
    promptDelayMs = 10000,
    promptDurationMs = 3000,
    requestCallbackText = "Request a callback",
    chatWithUsText = "Chat with us",
    closeText = "Close",
    requestCallbackIcon,
    chatWithUsIcon,
    closeIcon,
    menuItemIconStyles,
    closeIconStyles,
    menuStyles = {},
    menuCardStyles = {},
    menuItemStyles = {},
    closeButtonStyles = {},
    hideMenuOnOutsideClick = false,
    closeMenuSignal,
    suppressMenuOnClick = false,
  } = props;

  const { on_load, on_close, on_request_callback, on_chat_with_us } =
    actions;
  useOnload({ on_load, is_visible });

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (!collapseAfterMs || collapseAfterMs <= 0) return;
    const timer = setTimeout(() => setIsCollapsed(true), collapseAfterMs);
    return () => clearTimeout(timer);
  }, [collapseAfterMs]);

  useEffect(() => {
    if (!isCollapsed || !promptDelayMs || promptDelayMs <= 0) return;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    const delayTimer = setTimeout(() => {
      setShowPrompt(true);
      if (promptDurationMs > 0) {
        hideTimer = setTimeout(() => setShowPrompt(false), promptDurationMs);
      }
    }, promptDelayMs);
    return () => {
      clearTimeout(delayTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [isCollapsed, promptDelayMs, promptDurationMs]);

  useEffect(() => {
    if (!isMenuOpen || !hideMenuOnOutsideClick) return;
    const handleOutsideClick = () => setIsMenuOpen(false);
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [isMenuOpen, hideMenuOnOutsideClick]);

  useEffect(() => {
    if (!closeMenuSignal) return;
    executeActions(on_close);
    setIsMenuOpen(false);
    setShowPrompt(false);
  }, [closeMenuSignal, on_close]);

  useEffect(() => {
    if (isMenuOpen) {
      const { data, journey } = getRequestpayload();
      triggerAnalyticEvent({
        edata: {
          product: data?.product,
          view_name: "view_call_Floating_menu",
          view_type: "view_modal",
        },
        odata: { journey },
        event_name: "view",
      });
    }
  }, [isMenuOpen]);

  const fireFloatMenuAnalytic = useCallback((actionName: string) => {
    const { data, journey } = getRequestpayload();
    triggerAnalyticEvent({
      edata: { product: data?.product, action_name: actionName, action_type: "tap_btn" },
      odata: { journey },
      event_name: "action",
    });
  }, []);

  const handleClick = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      setShowPrompt(false);
      if (!suppressMenuOnClick) {
        setIsMenuOpen((prev) => {
          if (!prev) fireFloatMenuAnalytic("tap_btn_call_or_chat_widget");
          return !prev;
        });
      }
    },
    [suppressMenuOnClick, fireFloatMenuAnalytic]
  );

  const handleRequestCallback = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      fireFloatMenuAnalytic("tap_btn_call_float_menu");
      executeActions(on_request_callback);
    },
    [fireFloatMenuAnalytic, on_request_callback]
  );

  const handleChatWithUs = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      fireFloatMenuAnalytic("tap_btn_chat_float_menu");
      executeActions(on_chat_with_us);
      openChatbot();
    },
    [fireFloatMenuAnalytic, on_chat_with_us]
  );

  const handleCloseMenu = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      executeActions(on_close);
      setIsMenuOpen(false);
    },
    [on_close]
  );

  const renderMenuItem = (
    iconSrc: string | undefined,
    label: string,
    onClick: (e?: React.MouseEvent) => void,
    alt: string
  ) => (
    <Flex
      style={{ ...menuItemBaseStyles, ...menuItemStyles }}
      onClick={onClick}
    >
      {iconSrc && (
        <IconImage
          src={iconSrc}
          alt={alt}
          styles={menuItemIconStyles || DEFAULT_ICON_STYLES}
        />
      )}
      <span>{label}</span>
    </Flex>
  );

  if (!is_visible) return null;

  return (
    <Box style={{ ...wrapperBaseStyles, ...styles }}>
      {isMenuOpen && (
        <Box style={{ ...menuWrapperBaseStyles, ...menuStyles }}>
          <Box style={{ ...menuCardBaseStyles, ...menuCardStyles }}>
            {renderMenuItem(
              requestCallbackIcon,
              requestCallbackText,
              handleRequestCallback,
              "callback"
            )}
            {renderMenuItem(
              chatWithUsIcon,
              chatWithUsText,
              handleChatWithUs,
              "chat"
            )}
          </Box>
        </Box>
      )}
      {isMenuOpen ? (
        <Flex
          as="button"
          onClick={handleCloseMenu}
          style={{ ...closeButtonBaseStyles, ...closeButtonStyles }}
        >
          {closeIcon && (
            <IconImage
              src={closeIcon}
              alt="close"
              styles={closeIconStyles || DEFAULT_CLOSE_ICON_STYLES}
            />
          )}
          <span>{closeText}</span>
        </Flex>
      ) : showPrompt ? (
        <Flex style={{ gap: "8px", alignItems: "center" }}>
          <Flex
            as="button"
            onClick={handleClick}
            style={{ ...buttonBaseStyles, ...buttonStyles, ...promptButtonStyles }}
          >
            <Label styles={textStyles}>{promptText}</Label>
          </Flex>
          {icon && (
            <Flex
              as="button"
              onClick={handleClick}
              style={{ ...iconOnlyButtonBaseStyles, ...iconButtonStyles }}
            >
              <IconWrapper styles={iconWrapperStyles}>
                <IconImage
                  src={icon}
                  alt={iconAlt}
                  styles={iconStyles}
                />
              </IconWrapper>
            </Flex>
          )}
        </Flex>
      ) : (
        <Flex
          as="button"
          onClick={handleClick}
          style={{
            ...buttonBaseStyles,
            ...buttonStyles,
            ...(isCollapsed ? collapsedButtonBaseStyles : {}),
            ...(isCollapsed ? collapsedButtonStyles : {}),
          }}
        >
          {!isCollapsed && <Label styles={textStyles}>{text}</Label>}
          {icon && (
            <IconWrapper styles={iconWrapperStyles}>
              <IconImage
                src={icon}
                alt={iconAlt}
                styles={iconStyles}
              />
            </IconWrapper>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default connect(mapStateToProps)(FloatingIcon);
